import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faClock,
  faInfoCircle,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";

const ViewOwner = ({ owner, onClose }) => {
  if (!owner) return null;

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isActive = owner.status === "Active";

  const InfoItem = ({ icon, label, value, highlight = false }) => (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all duration-200 ${
        highlight
          ? "bg-blue-50 border-blue-200"
          : "bg-gray-50 border-gray-100 hover:bg-gray-100"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          highlight ? "bg-blue-100" : "bg-white border border-gray-200"
        }`}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`h-3.5 w-3.5 ${highlight ? "text-blue-600" : "text-gray-600"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm uppercase tracking-wider text-gray-600 font-medium">
          {label}
        </p>
        <p className="text-sm font-medium text-gray-900 break-words">
          {value || "-"}
        </p>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Owner details"
      maxWidth="sm:max-w-2xl"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold">
              Shop owner information
            </p>
            <p className="text-xs text-gray-600 font-medium">
              View owner account details
            </p>
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InfoItem
            icon={faUser}
            label="Full Name"
            value={owner.full_name || owner.owner_name}
            highlight={true}
          />
          <InfoItem
            icon={faEnvelope}
            label="Email Address"
            value={owner.email}
          />
          <InfoItem icon={faPhone} label="Phone Number" value={owner.phone} />

          {/* Status card — right column on md+, full width on small */}
          <div
            className={`rounded-lg p-3 border flex items-center gap-3 ${
              isActive ? "bg-white border-green-400" : "bg-white border-red-200"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                isActive ? "bg-green-100" : "bg-red-100"
              }`}
            >
              <FontAwesomeIcon
                icon={isActive ? faCheckCircle : faTimesCircle}
                className={`h-3.5 w-3.5 ${
                  isActive ? "text-green-600" : "text-red-600"
                }`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm uppercase tracking-wider text-gray-600 font-medium">
                Status
              </p>
              <p
                className={`text-sm font-semibold ${
                  isActive ? "text-green-700" : "text-red-700"
                }`}
              >
                {owner.status || "Unknown"}
              </p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
            Timeline
          </h4>

          {/* Large & medium: Added Date | Created + Updated */}
          <div className="hidden sm:grid grid-cols-2 gap-3">
            <InfoItem
              icon={faCalendarAlt}
              label="Added Date"
              value={formatDateShort(owner.added_date)}
            />
            <div className="flex flex-col gap-3">
              <InfoItem
                icon={faClock}
                label="Updated At"
                value={formatDate(owner.updated_at)}
              />
            </div>
          </div>

          {/* Small screens: two rows */}
          <div className="grid sm:hidden grid-cols-1 gap-3">
            <InfoItem
              icon={faCalendarAlt}
              label="Added Date"
              value={formatDateShort(owner.added_date)}
            />
            <div className="grid grid-cols-1 gap-3">
              <InfoItem
                icon={faClock}
                label="Updated At"
                value={formatDate(owner.updated_at)}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ViewOwner;
